import React, { useEffect, useState } from 'react';
import { StatisticsService } from '../../services/statisticsService';
import { usePlayerStore } from '../../stores/playerStore';
import { useMatchStore } from '../../stores/matchStore';
import { 
  ChartBarIcon,
  UserIcon,
  ClockIcon,
  TrophyIcon,
  ScaleIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

interface OverallStatsProps {
  className?: string;
}

interface RotationStatistic {
  playerId: string;
  playerName: string;
  playerNumber: number;
  restCount: number;
}

interface RotationStats {
  totalMatches: number;
  rotationStats: RotationStatistic[];
}

const OverallStats: React.FC<OverallStatsProps> = ({ className = '' }) => {
  const { players, statistics } = usePlayerStore();
  const { matchHistory } = useMatchStore();
  
  const [rotationStats, setRotationStats] = useState<RotationStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRotationStats();
  }, []);

  const loadRotationStats = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const stats = await StatisticsService.getRotationStatistics();
      setRotationStats(stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载轮换统计失败');
    } finally {
      setIsLoading(false);
    }
  };

  // 计算公平性指标
  const calculateFairnessMetrics = () => {
    if (!rotationStats || rotationStats.rotationStats.length === 0) {
      return { fairnessScore: 0, isBalanced: false, maxRest: 0, minRest: 0, avgRest: 0 };
    }

    const restCounts = rotationStats.rotationStats.map(stat => stat.restCount);
    const maxRest = Math.max(...restCounts);
    const minRest = Math.min(...restCounts);
    const avgRest = restCounts.reduce((sum, count) => sum + count, 0) / restCounts.length;
    
    // 公平性分数：基于休息次数的标准差
    const variance = restCounts.reduce((sum, count) => sum + Math.pow(count - avgRest, 2), 0) / restCounts.length;
    const standardDeviation = Math.sqrt(variance);
    
    // 公平性分数：标准差越小，公平性越高（满分100）
    const fairnessScore = Math.max(0, Math.round(100 - (standardDeviation * 20)));
    const isBalanced = maxRest - minRest <= 1; // 最大差距不超过1次认为是平衡的

    return { fairnessScore, isBalanced, maxRest, minRest, avgRest: Math.round(avgRest * 10) / 10 };
  };

  // 计算参赛频率
  const calculateParticipationStats = () => {
    if (!statistics || statistics.length === 0) {
      return { totalMatches: 0, avgMatches: 0, maxMatches: 0, minMatches: 0 };
    }

    const matchCounts = statistics.map(stat => stat.totalMatches);
    const totalMatches = matchCounts.reduce((sum, count) => sum + count, 0);
    const avgMatches = Math.round((totalMatches / statistics.length) * 10) / 10;
    const maxMatches = Math.max(...matchCounts);
    const minMatches = Math.min(...matchCounts);

    return { totalMatches, avgMatches, maxMatches, minMatches };
  };

  const fairnessMetrics = calculateFairnessMetrics();
  const participationStats = calculateParticipationStats();

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">总体统计</h2>
        <button
          onClick={loadRotationStats}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          刷新
        </button>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="text-red-600 text-sm">{error}</div>
        </div>
      )}

      {/* 总体概览 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <OverviewCard
          title="总比赛数"
          value={matchHistory.length}
          icon={<TrophyIcon className="w-6 h-6 text-blue-600" />}
          color="bg-blue-50"
        />
        <OverviewCard
          title="活跃玩家"
          value={players.length}
          icon={<UserIcon className="w-6 h-6 text-green-600" />}
          color="bg-green-50"
        />
        <OverviewCard
          title="平均参赛"
          value={`${participationStats.avgMatches}场`}
          icon={<ChartBarIcon className="w-6 h-6 text-purple-600" />}
          color="bg-purple-50"
        />
        <OverviewCard
          title="公平性分数"
          value={`${fairnessMetrics.fairnessScore}分`}
          icon={<ScaleIcon className="w-6 h-6 text-orange-600" />}
          color="bg-orange-50"
        />
      </div>

      {/* 轮换公平性分析 */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">轮换公平性分析</h3>
        
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">公平性评估</span>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                fairnessMetrics.isBalanced ? 'bg-green-500' : 'bg-yellow-500'
              }`}></div>
              <span className="text-sm font-medium">
                {fairnessMetrics.isBalanced ? '平衡' : '需调整'}
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-lg font-bold text-gray-800">
                {fairnessMetrics.fairnessScore}
              </div>
              <div className="text-xs text-gray-600">公平性分数</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">
                {fairnessMetrics.maxRest - fairnessMetrics.minRest}
              </div>
              <div className="text-xs text-gray-600">最大差距</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">
                {fairnessMetrics.avgRest}
              </div>
              <div className="text-xs text-gray-600">平均休息</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">
                {rotationStats?.totalMatches || 0}
              </div>
              <div className="text-xs text-gray-600">轮换次数</div>
            </div>
          </div>
        </div>

        {/* 休息次数统计 */}
        {rotationStats && rotationStats.rotationStats.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium text-gray-700">各玩家休息统计</h4>
            {rotationStats.rotationStats.map((stat) => (
              <RestStatCard 
                key={stat.playerId}
                stat={stat}
                maxRest={fairnessMetrics.maxRest}
                avgRest={fairnessMetrics.avgRest}
              />
            ))}
          </div>
        )}
      </div>

      {/* 参赛频率分析 */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">参赛频率分析</h3>
        
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-lg font-bold text-blue-600">
                {participationStats.maxMatches}
              </div>
              <div className="text-xs text-gray-600">最多参赛</div>
            </div>
            <div>
              <div className="text-lg font-bold text-green-600">
                {participationStats.avgMatches}
              </div>
              <div className="text-xs text-gray-600">平均参赛</div>
            </div>
            <div>
              <div className="text-lg font-bold text-orange-600">
                {participationStats.minMatches}
              </div>
              <div className="text-xs text-gray-600">最少参赛</div>
            </div>
            <div>
              <div className="text-lg font-bold text-purple-600">
                {participationStats.maxMatches - participationStats.minMatches}
              </div>
              <div className="text-xs text-gray-600">参赛差距</div>
            </div>
          </div>
        </div>

        {/* 参赛次数分布 */}
        {statistics.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium text-gray-700">各玩家参赛统计</h4>
            {statistics
              .sort((a, b) => b.totalMatches - a.totalMatches)
              .map((stat) => {
                const player = players.find(p => p.id === stat.playerId);
                return player ? (
                  <ParticipationStatCard 
                    key={stat.playerId}
                    player={player}
                    stat={stat}
                    maxMatches={participationStats.maxMatches}
                  />
                ) : null;
              })}
          </div>
        )}
      </div>

      {/* 建议和提示 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">系统建议</h4>
        <div className="text-sm text-blue-700 space-y-1">
          {fairnessMetrics.isBalanced ? (
            <div>✅ 当前轮换很平衡，继续保持！</div>
          ) : (
            <div>⚠️ 建议调整轮换顺序以提高公平性</div>
          )}
          
          {participationStats.maxMatches - participationStats.minMatches > 2 && (
            <div>📊 参赛次数差距较大，建议关注参赛频率较低的玩家</div>
          )}
          
          {matchHistory.length < 10 && (
            <div>🎯 比赛数据较少，随着比赛增加统计会更准确</div>
          )}
        </div>
      </div>

      {/* 说明信息 */}
      <div className="text-xs text-gray-500 mt-6 pt-4 border-t">
        <div className="space-y-1">
          <div>• 公平性分数基于休息次数的均衡程度计算</div>
          <div>• 轮换统计帮助确保每个人都有公平的休息机会</div>
          <div>• 参赛频率反映每个人的活跃程度</div>
        </div>
      </div>
    </div>
  );
};

interface OverviewCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}

const OverviewCard: React.FC<OverviewCardProps> = ({ title, value, icon, color }) => {
  return (
    <div className={`${color} rounded-lg p-4`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-2xl font-bold text-gray-800 mb-1">
            {value}
          </div>
          <div className="text-sm text-gray-600">
            {title}
          </div>
        </div>
        <div className="opacity-80">
          {icon}
        </div>
      </div>
    </div>
  );
};

interface RestStatCardProps {
  stat: RotationStatistic;
  maxRest: number;
  avgRest: number;
}

const RestStatCard: React.FC<RestStatCardProps> = ({ stat, maxRest, avgRest }) => {
  const isAboveAverage = stat.restCount > avgRest;
  const isBelowAverage = stat.restCount < avgRest;
  
  return (
    <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center space-x-3">
        <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
          {stat.playerNumber}
        </div>
        <div>
          <div className="font-medium text-gray-800">{stat.playerName}</div>
          <div className="text-sm text-gray-600">编号: {stat.playerNumber}</div>
        </div>
      </div>
      
      <div className="flex items-center space-x-4">
        <div className="text-right">
          <div className="text-lg font-bold text-gray-800">
            {stat.restCount}
          </div>
          <div className="text-xs text-gray-600">休息次数</div>
        </div>
        
        <div className="flex items-center space-x-1">
          <ClockIcon className="w-4 h-4 text-gray-400" />
          {isAboveAverage && (
            <span className="text-xs text-orange-600">偏多</span>
          )}
          {isBelowAverage && (
            <span className="text-xs text-blue-600">偏少</span>
          )}
          {!isAboveAverage && !isBelowAverage && (
            <span className="text-xs text-green-600">平均</span>
          )}
        </div>
      </div>
    </div>
  );
};

interface ParticipationStatCardProps {
  player: any;
  stat: any;
  maxMatches: number;
}

const ParticipationStatCard: React.FC<ParticipationStatCardProps> = ({ 
  player, 
  stat, 
  maxMatches 
}) => {
  const participationRate = maxMatches > 0 ? Math.round((stat.totalMatches / maxMatches) * 100) : 0;
  const winRate = stat.totalMatches > 0 ? Math.round((stat.wins / stat.totalMatches) * 100) : 0;
  
  return (
    <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center space-x-3">
        <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
          {player.number}
        </div>
        <div>
          <div className="font-medium text-gray-800">{player.name}</div>
          <div className="text-sm text-gray-600">
            {stat.totalMatches} 场比赛 • 胜率 {winRate}%
          </div>
        </div>
      </div>
      
      <div className="text-right">
        <div className="text-lg font-bold text-gray-800">
          {participationRate}%
        </div>
        <div className="text-xs text-gray-600">参与度</div>
      </div>
    </div>
  );
};

export default OverallStats;